import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  can,
  canAny,
  canSeeReport,
  TV_SCREEN_INFO,
  TV_SCREENS,
  type AuthenticatedUser,
  type LoginResponse,
  type Permission,
  type ReportKey,
  type UserRole,
} from '@codepass/shared';
import { ApiError, api, onForbidden, onUnauthorized } from '@/lib/api';
import { clearBrowserSession, supabaseBrowserClient } from '@/lib/supabase';

/**
 * Oversaetter Supabase' egne fejl fra kodeombytningen til noget brugervendt og dansk.
 *
 * supabase-js' beskeder er engelske og skrevet til en anden slags app: PKCE-fejlen raader
 * én til at bruge @supabase/ssr i Next.js eller SvelteKit, hvilket hverken er rammen her
 * eller noget en controller kan gøre noget ved. Sprogkonventionen siger desuden dansk paa
 * alt brugervendt.
 */
export function danishOAuthError(message: string | undefined): string {
  const lowered = (message ?? '').toLowerCase();

  /*
   * Verifieren ligger i localStorage og bruges op ved foerste ombytning. Er den vaek, er
   * forsoeget enten allerede gennemfoert, paabegyndt i en anden browser, eller ogsaa er
   * callback-siden blevet genindlaest. Alle tre har samme udvej: start forfra fra /login.
   */
  if (
    lowered.includes('code verifier') ||
    lowered.includes('code_verifier') ||
    lowered.includes('code challenge')
  ) {
    return (
      'Login-forsøget kunne ikke fuldføres. Det er enten allerede brugt, påbegyndt i en ' +
      'anden browser, eller også blev siden genindlæst. Gå tilbage til login og prøv forfra ' +
      '— en genindlæsning af denne side vil fejle igen.'
    );
  }

  if (lowered.includes('expired')) {
    return 'Login-forsøget er udløbet. Gå tilbage til login og prøv forfra.';
  }

  return 'Kunne ikke fuldføre login mod Microsoft. Gå tilbage til login og prøv forfra.';
}

/**
 * Der er ingen `mustChangePassword` her laengere. Login gaar gennem Microsoft, saa
 * adgangskoden hoerer til Microsoft-kontoen, og baade skiftesiden og det tvungne skift er
 * fjernet. API'et sender stadig `must_change_password` i login-svaret; feltet laeses bare
 * ikke af nogen.
 */
interface AuthState {
  user: AuthenticatedUser | null;
  loading: boolean;
  /**
   * Grunden til at sessionen blev afbrudt - fx en deaktiveret konto. Vises paa
   * login-siden, saa brugeren ikke bare lander paa et tomt loginbillede.
   */
  authMessage: string | null;
  clearAuthMessage: () => void;
  login: (email: string, password: string) => Promise<void>;
  /** Sender browseren videre til Microsoft. Intet efter kaldet naar at koere. */
  loginWithMicrosoft: () => Promise<void>;
  /** Anden halvdel af OAuth-floewet. Kaldes af /auth/callback med koden fra URL'en. */
  completeOAuthLogin: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasRole: (...roles: UserRole[]) => boolean;
  /**
   * Om brugeren har rettigheden - rollernes rettigheder, som serveren sendte dem. De rettes paa
   * /admin/roller; se PERMISSIONS i @codepass/shared.
   */
  can: (permission: Permission) => boolean;
  /** Mindst én af rettighederne. */
  canAny: (...permissions: Permission[]) => boolean;
  /** Om brugerens roller kan se rapporten. */
  canSeeReport: (key: ReportKey) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

/** Skal matche ruten i App.tsx og redirect-URL'en i Supabase. */
const OAUTH_CALLBACK_PATH = '/auth/callback';

/**
 * Storskaermene har ingen session og faar ingen - de har et token i adressen, se
 * pages/TvDashboard.tsx. Et probe-kald derfra ville bare 401'e og udloese et forgaeves refresh,
 * hver gang skaermen starter. Samme stier som ruterne i App.tsx - `TV_SCREEN_INFO`.
 */
const TV_PATHS: readonly string[] = TV_SCREENS.map((screen) => TV_SCREEN_INFO[screen].path);

/** Sider, hvor der ikke skal slaas en session op. Se de to effekter nedenfor. */
function isSessionlessPage(): boolean {
  const path = window.location.pathname.replace(/\/+$/, '');
  return path === OAUTH_CALLBACK_PATH || TV_PATHS.includes(path);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [authMessage, setAuthMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const loadSession = useCallback(async () => {
    try {
      const response = await api.get<{ user: AuthenticatedUser }>('/auth/me');
      setUser(response.user);
    } catch (error) {
      // 401 ved opstart er normalt: brugeren er ikke logget ind endnu. Andre fejl er
      // det ikke, og skal vaere synlige i konsollen frem for at se ud som udlogning.
      if (!(error instanceof ApiError) || error.status !== 401) {
        console.error('Kunne ikke hente session', error);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    /**
     * Paa OAuth-callbacket er der ingen session at genskabe - den er ved at blive
     * oprettet nogle linjer laengere nede.
     *
     * Et probe-kald her ville 401'e, udloese et forgaeves refresh, og dets
     * onUnauthorized kunne naa frem *efter* completeOAuthLogin har sat brugeren. Det
     * ville nulstille et login der lige var lykkedes, og guarden ville sende brugeren
     * til /login igen - et kapløb der afhaenger af hvor hurtigt Supabase svarer, og
     * derfor kun fejler nogle gange.
     *
     * TV-dashboardet springer det over af den enklere grund, at der aldrig er en session.
     */
    if (isSessionlessPage()) {
      setLoading(false);
      return;
    }

    void loadSession();
  }, [loadSession]);

  // Naar en refresh endeligt fejler, ryddes tilstanden her - ellers ville UI vise et
  // indlogget skelet uden data.
  useEffect(
    () =>
      onUnauthorized(() => {
        setUser(null);
        queryClient.clear();
      }),
    [queryClient],
  );

  /**
   * Henter profilen igen uden at logge brugeren ud ved forbigaaende fejl.
   *
   * loadSession rydder brugeren ved enhver fejl, hvilket er rigtigt ved opstart, men
   * forkert i baggrunden: et enkelt netvaerksglip ville smide en arbejdende bruger ud.
   * Kun serverens egne afvisninger taeller her.
   */
  const syncSession = useCallback(async () => {
    try {
      const response = await api.get<{ user: AuthenticatedUser }>('/auth/me');
      setUser(response.user);
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        setUser(null);
      }
    }
  }, []);

  /**
   * Serveren er autoritativ paa rolle og status, og de kan aendre sig midt i en session.
   *
   * En 403 er den foerste besked klienten faar om det. Er kontoen lukket, ryddes
   * sessionen og brugeren sendes til login med serverens forklaring. Ellers er det
   * sandsynligvis rollen der er skiftet, og profilen hentes igen saa menu og guards
   * retter sig ind efter den nye.
   */
  useEffect(
    () =>
      onForbidden((error, path) => {
        if (error.code === 'ACCOUNT_DEACTIVATED') {
          setUser(null);
          setAuthMessage(error.message);
          queryClient.clear();
          // Ryd cookies, saa et reload ikke lander i samme afvisning igen.
          void api.post('/auth/logout').catch(() => null);
          return;
        }

        // Ikke paa /auth/me: den 403 kommer *fra* opslaget, og et nyt ville koere i ring.
        if (path !== '/auth/me') void syncSession();
      }),
    [queryClient, syncSession],
  );

  /**
   * Rollen kan ogsaa vaere aendret uden at brugeren har roert noget. Et tjek naar fanen
   * faar fokus igen er nok til at menuen ikke staar og lyver.
   *
   * Ikke paa OAuth-callbacket: browseren giver fanen fokus naar den vender tilbage fra
   * Microsoft, og der er endnu ingen cookie. Opslaget ville 401'e, udloese et forgaeves
   * refresh, og rydde en bruger completeOAuthLogin lige havde sat - samme kapløb som
   * probe-kaldet ovenfor undgaar.
   */
  useEffect(() => {
    if (isSessionlessPage()) return;

    const onFocus = (): void => void syncSession();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [syncSession]);

  const login = useCallback(async (email: string, password: string) => {
    const response = await api.post<LoginResponse>('/auth/login', { email, password });
    setUser(response.user);
  }, []);

  /**
   * Foerste halvdel af Microsoft-login. Kaldet ender i en redirect til Microsoft, saa
   * funktionen returnerer normalt ikke - kun hvis Supabase afviser med det samme.
   */
  const loginWithMicrosoft = useCallback(async () => {
    const { error } = await supabaseBrowserClient().auth.signInWithOAuth({
      provider: 'azure',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        // email skal med: profilen bindes til kontoen paa email ved foerste login.
        scopes: 'openid profile email',
      },
    });

    if (error) throw new Error(error.message);
  }, []);

  /**
   * Anden halvdel. Koden byttes til en session, som afleveres til API'et og bliver til
   * HTTP-only cookies. Browserens egen kopi ryddes bagefter - uanset udfald - saa der
   * ikke ligger et token i localStorage som ingen bruger.
   *
   * Bemaerk at oprydningen i `finally` ogsaa loeber, naar API-kaldet fejler. Det er med
   * vilje - et ubrugt token maa ikke blive liggende - men det betyder at et mislykket
   * forsoeg ikke kan genforsoeges paa den samme callback-URL: verifieren er vaek, og
   * koden er brugt. Derfor skal fejlbeskeden sende brugeren tilbage til /login frem for
   * at invitere til en genindlaesning, som ville fejle igen.
   */
  const completeOAuthLogin = useCallback(async (code: string) => {
    const { data, error } = await supabaseBrowserClient().auth.exchangeCodeForSession(code);

    if (error || !data.session) {
      // Den tekniske årsag hører i konsollen, ikke på skærmen — den danske besked nedenfor
      // er det brugeren kan handle på.
      if (error) console.warn('Supabase afviste kodeombytningen:', error.message);
      clearBrowserSession();
      throw new Error(danishOAuthError(error?.message));
    }

    try {
      const response = await api.post<LoginResponse>('/auth/session', {
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      });
      setUser(response.user);
    } finally {
      clearBrowserSession();
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setUser(null);
      queryClient.clear();
    }
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      authMessage,
      clearAuthMessage: () => setAuthMessage(null),
      login,
      loginWithMicrosoft,
      completeOAuthLogin,
      logout,
      refreshUser: loadSession,
      hasRole: (...roles) => (user ? roles.includes(user.role) : false),
      can: (permission) => can(user, permission),
      canAny: (...permissions) => canAny(user, permissions),
      canSeeReport: (key) => canSeeReport(user, key),
    }),
    [
      user,
      loading,
      authMessage,
      login,
      loginWithMicrosoft,
      completeOAuthLogin,
      logout,
      loadSession,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth skal bruges inden i AuthProvider');
  return context;
}
